import { authFetch } from '../../../api/csrf';
import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  handleTextInputChange,
  handleSearchInputChange,
  getCharacterCount,
  validateRequiredFields,
  FIELD_LIMITS
} from '../../../utils/inputValidation';
import { truncateText, formatDate, capitalizeWords, capitalizeSentences } from '../../../utils/stringUtils';
import ticketService from '../../../api/crm/support/ticketService';
import customerService from '../../../api/crm/customers/customerService';
import orderService from '../../../api/crm/orders/orderService';
import userService from '../../../api/users/userService';
import teamManagementService from '../../../api/crm/team-management/teamManagementService';
import { useAuth } from '../../../context/AuthContext';
import ExportModal from '../../../components/common/ExportModal';
import { generateExportFilename, downloadFromBackend, exportToCSV } from '../../../utils/exportUtils';
import { API_ENDPOINTS } from '../../../api/config';
import {
  Plus,
  Search,
  Filter,
  Clock,
  AlertTriangle,
  CheckCircle,
  XCircle,
  User,
  Calendar,
  Tag,
  MessageSquare,
  FileText,
  Download,
  Edit,
  Trash2,
  Eye,
  TrendingUp,
  Users,
  Settings,
  Bell,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  MoreVertical,
  Phone,
  Mail,
  MessageCircle,
  ExternalLink,
  Archive,
  RefreshCw,
  X,
  Upload,
  Paperclip,
  ArrowLeft,
  ChevronDown,
} from "lucide-react";
import Button from '../../../components/common/Button/Button';
import PermissionGate from '../../../components/common/PermissionGate';
import { USER_ROLES } from '../../../utils/constants';
import SearchableDropdown from '../../../components/common/SearchableDropdown/SearchableDropdown';
import DateRangeFilter from '../../../components/common/DateRangeFilter';
import ConfirmationDialog from '../../../components/common/ConfirmationDialog';
import BulkTicketUpload from './BulkTicketUpload';
import SelectedExportFormatBar from '../../../components/common/SelectedExportFormatBar';
import { useCallback } from "react";

const TicketsFeedback = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const today = new Date().toISOString().split('T')[0];

  const { user } = useAuth();
  const isSalesExecutive = user?.role === 'sales_executive' || user?.role === 'export_executive' || user?.role === 'business_relationship_manager' || user?.role === 'business_development_executive';
  const isManager = user?.role === 'sales_manager' || user?.role === 'export_manager';

  const currentUserId =
    user?.id ||
    user?.userId ||
    (() => {
      try {
        const persisted = JSON.parse(localStorage.getItem('user_data') || '{}');
        return persisted?.id || persisted?.userId || persisted?._id;
      } catch (e) {
        return user?._id;
      }
    })();

  // Assignee filter state
  const defaultAssignee = isManager && currentUserId ? currentUserId : 'all';

  // Initial filter values from URL (for persistence across refresh)
  const initialSearch = searchParams.get('search') || '';
  const initialStatus = searchParams.get('status') || 'all';
  const initialPriority = searchParams.get('priority') || 'all';
  const initialAssignee = searchParams.get('assignee') || defaultAssignee;
  const initialStartDate = searchParams.get('startDate') || '';
  const initialEndDate = searchParams.get('endDate') || '';
  const initialPage = Number(searchParams.get('page')) || 1;
  const initialLimit = Number(searchParams.get('limit')) || 10;
  const initialSortBy = searchParams.get('sortBy') || 'created';
  const initialSortDir = searchParams.get('sortOrder') || 'desc';
  const isInitialSearchResetMount = useRef(true);
  const isInitialFilterResetMount = useRef(true);

  const [filterAssignee, setFilterAssignee] = useState(initialAssignee);
  const [assigneeOptions, setAssigneeOptions] = useState([]);
  const [assigneeFilterSearchTerm, setAssigneeFilterSearchTerm] = useState('');
  const [debouncedAssigneeSearchTerm, setDebouncedAssigneeSearchTerm] = useState('');
  const [openAssigneeDropdown, setOpenAssigneeDropdown] = useState(false);
  const [assigneeDropdownPosition, setAssigneeDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const assigneeDropdownRef = useRef(null);

  // Debounce assignee search input
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedAssigneeSearchTerm(assigneeFilterSearchTerm.trim());
    }, 300);
    return () => clearTimeout(handler);
  }, [assigneeFilterSearchTerm]);

  // React Query: Fetch CRM users for assignee filter with backend search
  const {
    data: assigneeUsersData,
    isLoading: loadingAssigneeUsers,
    isFetching: isFetchingAssigneeUsers,
  } = useQuery({
    queryKey: [
      'crmAssigneeUsers',
      isManager ? (currentUserId || 'manager') : 'all',
      debouncedAssigneeSearchTerm,
    ],
    queryFn: async () => {
      const params = {
        roleFilter: 'sales',
        limit: 100,
        ...(debouncedAssigneeSearchTerm ? { search: debouncedAssigneeSearchTerm } : {}),
      };

      if (isManager) {
        params.managerId = currentUserId;
      }

      const result = await teamManagementService.getCRMUsers(params);
      return result?.success && result.users ? result.users : [];
    },
    enabled: !isSalesExecutive && (isManager ? !!currentUserId : true),
    staleTime: 0,
  });

  const loadingAssigneeFilter = loadingAssigneeUsers || isFetchingAssigneeUsers;

  // Derive filtered assignee options based on fetched data and search term
  const filteredAssigneeOptions = useMemo(() => {
    if (!assigneeUsersData) return [];

    const lowerTerm = debouncedAssigneeSearchTerm.toLowerCase();

    if (isManager) {
      const executives = assigneeUsersData
        .filter(u => ['sales_executive', 'export_executive', 'business_relationship_manager', 'business_development_executive'].includes(u.role))
        .map(u => ({
          id: u.user || u._id || u.id,
          name: u.name || `${u.firstName || ''} ${u.lastName || ''}`.trim() || 'Unknown',
          email: u.email || '',
          role: u.role || ''
        }))
        .sort((a, b) => a.name.localeCompare(b.name));

      const selfOption = {
        id: currentUserId,
        name: `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || user?.name || 'Me',
        email: user?.email || '',
        role: user?.role || ''
      };

      const myTeamOption = {
        id: 'my_team',
        name: 'My Team',
        email: '',
        role: 'system'
      };

      const prefixOptions = [];
      if (!debouncedAssigneeSearchTerm || 'my team'.includes(lowerTerm)) {
        prefixOptions.push(myTeamOption);
      }
      if (!debouncedAssigneeSearchTerm || 'unassigned'.includes(lowerTerm)) {
        prefixOptions.push({ id: 'unassigned', name: 'Unassigned', role: 'system' });
      }
      if (!debouncedAssigneeSearchTerm || selfOption.name.toLowerCase().includes(lowerTerm) || (selfOption.email && selfOption.email.toLowerCase().includes(lowerTerm))) {
        prefixOptions.push(selfOption);
      }

      return [...prefixOptions, ...executives];
    } else {
      const mappedUsers = assigneeUsersData.map(u => ({
        id: u.user || u._id || u.id,
        name: u.name || `${u.firstName || ''} ${u.lastName || ''}`.trim() || 'Unknown',
        email: u.email || '',
        role: u.role || ''
      })).sort((a, b) => a.name.localeCompare(b.name));

      const prefixOptions = [];
      if (!debouncedAssigneeSearchTerm || 'unassigned'.includes(lowerTerm)) {
        prefixOptions.push({ id: 'unassigned', name: 'Unassigned', role: 'system' });
      }

      return [...prefixOptions, ...mappedUsers];
    }
  }, [assigneeUsersData, debouncedAssigneeSearchTerm, isManager, currentUserId, user]);

  // Maintain assigneeOptions cache for button display label
  useEffect(() => {
    if (filteredAssigneeOptions.length > 0) {
      setAssigneeOptions(prev => {
        const map = new Map();
        prev.forEach(item => { if (item && item.id) map.set(item.id, item); });
        filteredAssigneeOptions.forEach(item => { if (item && item.id) map.set(item.id, item); });
        if (map.size === prev.length) return prev;
        return Array.from(map.values());
      });
    }
  }, [filteredAssigneeOptions]);

  useEffect(() => {
    if (openAssigneeDropdown && assigneeDropdownRef.current) {
      const rect = assigneeDropdownRef.current.getBoundingClientRect();
      setAssigneeDropdownPosition({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
        width: rect.width
      });
    }
  }, [openAssigneeDropdown]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (assigneeDropdownRef.current && !assigneeDropdownRef.current.contains(event.target)) {
        const dropdownElement = document.querySelector('[data-dropdown-assignee-filter]');
        if (!dropdownElement || !dropdownElement.contains(event.target)) {
          setOpenAssigneeDropdown(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Export State
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [showInlineSelectedFormats, setShowInlineSelectedFormats] = useState(false);
  const [inlineFormatPanelEntered, setInlineFormatPanelEntered] = useState(false);
  const selectedExportWrapRef = useRef(null);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingTicket, setEditingTicket] = useState(null);
  const [ticketIdForDetail, setTicketIdForDetail] = useState(null);
  const [searchTerm, setSearchTerm] = useState(initialSearch);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(initialSearch);
  const [errors, setErrors] = useState({});
  const searchInputRef = useRef(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [showBulkUpload, setShowBulkUpload] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [itemsPerPage, setItemsPerPage] = useState(initialLimit);

  // Filter state
  const [filterStatus, setFilterStatus] = useState(initialStatus);
  const [filterPriority, setFilterPriority] = useState(initialPriority);
  const [dateRange, setDateRange] = useState({ startDate: initialStartDate, endDate: initialEndDate });

  // Sorting state
  const [sortColumn, setSortColumn] = useState(initialSortBy);
  const [sortDirection, setSortDirection] = useState(initialSortDir);

  // Customer search and order fetch states
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedAssignee, setSelectedAssignee] = useState(null);
  const [editingAssignee, setEditingAssignee] = useState(null);

  // Modal assignee dropdown state (for Create/Edit ticket modals)
  const [modalAssigneeOptions, setModalAssigneeOptions] = useState([]);
  const [filteredModalAssigneeOptions, setFilteredModalAssigneeOptions] = useState([]);
  const [modalAssigneeSearchTerm, setModalAssigneeSearchTerm] = useState('');
  const [loadingModalAssignees, setLoadingModalAssignees] = useState(false);
  const [openModalAssigneeDropdown, setOpenModalAssigneeDropdown] = useState(false);
  const [modalAssigneeDropdownPosition, setModalAssigneeDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const modalAssigneeDropdownRef = useRef(null);
  const [openEditAssigneeDropdown, setOpenEditAssigneeDropdown] = useState(false);
  const [editAssigneeDropdownPosition, setEditAssigneeDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const editAssigneeDropdownRef = useRef(null);

  useEffect(() => {
    if (selectedRows.size === 0) {
      setShowInlineSelectedFormats(false);
    }
  }, [selectedRows.size]);

  useEffect(() => {
    if (!showInlineSelectedFormats) {
      setInlineFormatPanelEntered(false);
      return;
    }

    const raf = requestAnimationFrame(() => {
      setInlineFormatPanelEntered(true);
    });

    const handleClickOutside = (event) => {
      if (
        selectedExportWrapRef.current &&
        !selectedExportWrapRef.current.contains(event.target)
      ) {
        setShowInlineSelectedFormats(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showInlineSelectedFormats]);

  const {
    data: kpisData,
    isLoading: loadingKPIs
  } = useQuery({
    queryKey: ['ticketKPIs', filterStatus, filterAssignee],
    queryFn: async () => {
      const result = await ticketService.getTicketKPIs(
        filterStatus !== 'all' ? filterStatus : undefined,
        filterAssignee !== 'all' ? filterAssignee : undefined
      );
      if (result.success) {
        return {
          totalTickets: result.totalTickets || 0,
          openTickets: result.openTickets || 0,
          inProgressTickets: result.inProgressTickets || 0,
          pendingTickets: result.pendingTickets || 0,
          resolvedTickets: result.resolvedTickets || 0,
          closedTickets: result.closedTickets || 0
        };
      }
      throw new Error(result.error || 'Failed to load KPIs');
    }
  });

  const kpis = kpisData || {
    totalTickets: 0,
    openTickets: 0,
    inProgressTickets: 0,
    pendingTickets: 0,
    resolvedTickets: 0,
    closedTickets: 0
  };

  // React Query: Fetch tickets with pagination and search
  const {
    data: ticketsData,
    isLoading: loadingTickets,
    isError: ticketsError,
    error: ticketsErrorData
  } = useQuery({
    queryKey: ['tickets', debouncedSearchTerm, filterStatus, filterPriority, currentPage, itemsPerPage, dateRange, filterAssignee],
    queryFn: async () => {
      const filters = {
        search: debouncedSearchTerm.trim() || undefined,
        status: filterStatus !== 'all' ? filterStatus : undefined,
        priority: filterPriority !== 'all' ? filterPriority : undefined,
        startDate: dateRange.startDate || undefined,
        endDate: dateRange.endDate || undefined,
        page: currentPage,
        limit: itemsPerPage,
        assignee: filterAssignee !== 'all' ? filterAssignee : undefined
      };

      const result = await ticketService.getTickets(filters);

      if (result.success) {
        // Transform API response to match component format
        const transformedTickets = (result.data || []).map(ticket => ({
          id: ticket._id,
          ticketId: ticket.ticketId,
          title: ticket.title,
          description: ticket.description,
          customer: ticket.customerName,
          business: ticket.businessName || '',
          priority: ticket.priority,
          status: ticket.status,
          category: ticket.category || '',
          subCategory: ticket.subCategory || '',
          source: ticket.source,
          assignee: ticket.assignee ? `${ticket.assignee.firstName} ${ticket.assignee.lastName}` : '',
          assigneeEmail: ticket.assignee?.email || '',
          assigneeId: ticket.assignee?._id || null,
          created: ticket.createdAt,
          updated: ticket.updatedAt,
          tags: ticket.tags || [],
          attachments: ticket.attachments || [],
          linkedOrder: ticket.invoiceId ? (typeof ticket.invoiceId === 'object' ? (ticket.invoiceId.invoiceNumber || ticket.invoiceId._id) : ticket.invoiceId) : (ticket.orderId ? (typeof ticket.orderId === 'object' ? (ticket.orderId.orderNumber || ticket.orderId.orderId) : ticket.orderId) : ''),
          linkedOrderId: ticket.invoiceId ? (typeof ticket.invoiceId === 'object' ? (ticket.invoiceId._id || ticket.invoiceId.id) : ticket.invoiceId) : (ticket.orderId ? (typeof ticket.orderId === 'object' ? (ticket.orderId._id || ticket.orderId.id) : ticket.orderId) : ''),
          linkedInvoiceId: ticket.invoiceId ? (typeof ticket.invoiceId === 'object' ? (ticket.invoiceId._id || ticket.invoiceId.id) : ticket.invoiceId) : null,
          resolution: ticket.resolution || null,
          timeline: ticket.timeline || [],
          _id: ticket._id,
          customerId: (ticket.customerId && ticket.customerId._id) || ticket.customerId || null,
          dueDate: ticket.dueDate || null
        }));

        return {
          tickets: transformedTickets,
          pagination: {
            currentPage: result.page || 1,
            totalPages: result.totalPages || 1,
            totalItems: result.totalItems || 0,
            limit: result.limit || itemsPerPage,
            hasNextPage: result.hasNextPage || false,
            hasPrevPage: result.hasPrevPage || false
          }
        };
      } else {
        throw new Error(result.error || 'Failed to load tickets');
      }
    },
    keepPreviousData: true
  });

  const tickets = ticketsData?.tickets || [];
  const pagination = ticketsData?.pagination || null;

  // React Query: Fetch customer invoices
  const customerId = selectedCustomer?._id || selectedCustomer?.id || null;
  const {
    data: customerInvoicesData,
    isLoading: loadingInvoices
  } = useQuery({
    queryKey: ['customerInvoices', customerId],
    queryFn: async () => {
      if (!customerId) return [];
      const result = await customerService.getCustomerInvoices(customerId);
      if (result.success) {
        return result.invoices || [];
      }
      throw new Error(result.error || 'Failed to load invoices');
    },
    enabled: !!customerId
  });

  const customerInvoices = customerInvoicesData || [];

  // Autofocus search bar on mount
  useEffect(() => {
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, []);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      // Skip the page reset on the initial mount so the URL-restored page is kept
      if (isInitialSearchResetMount.current) {
        isInitialSearchResetMount.current = false;
        return;
      }
      setCurrentPage(1); // Reset to first page on search
      setSelectedRows(new Set()); // Reset selection on search
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Reset to first page when filter changes (skip on initial mount)
  useEffect(() => {
    if (isInitialFilterResetMount.current) {
      isInitialFilterResetMount.current = false;
      return;
    }
    setCurrentPage(1);
  }, [filterStatus, filterPriority]);

  // Sync filter/pagination/sort state to URL for persistence across refresh
  useEffect(() => {
    setSearchParams(prev => {
      const params = new URLSearchParams(prev);
      const managedKeys = ['search', 'status', 'priority', 'assignee', 'startDate', 'endDate', 'page', 'limit', 'sortBy', 'sortOrder'];
      managedKeys.forEach(key => params.delete(key));

      if (debouncedSearchTerm.trim()) params.set('search', debouncedSearchTerm.trim());
      if (filterStatus && filterStatus !== 'all') params.set('status', filterStatus);
      if (filterPriority && filterPriority !== 'all') params.set('priority', filterPriority);
      if (filterAssignee && filterAssignee !== defaultAssignee) params.set('assignee', filterAssignee);
      if (dateRange.startDate) params.set('startDate', dateRange.startDate);
      if (dateRange.endDate) params.set('endDate', dateRange.endDate);
      if (currentPage && currentPage !== 1) params.set('page', String(currentPage));
      if (itemsPerPage && itemsPerPage !== 10) params.set('limit', String(itemsPerPage));
      if (sortColumn && sortColumn !== 'created') params.set('sortBy', sortColumn);
      if (sortDirection && sortDirection !== 'desc') params.set('sortOrder', sortDirection);

      return params;
    }, { replace: true });
  }, [
    debouncedSearchTerm,
    filterStatus,
    filterPriority,
    filterAssignee,
    dateRange,
    currentPage,
    itemsPerPage,
    sortColumn,
    sortDirection,
    defaultAssignee,
    setSearchParams,
  ]);

  // Fetch functions for SearchableDropdown

  // Fetch top 6 customers (default)
  const fetchTopCustomers = async () => {
    try {
      const result = await customerService.getCustomers({ limit: 6 });
      if (result.success) {
        return result.customers || [];
      }
      return [];
    } catch (error) {
      return [];
    }
  };

  // Fetch customers by search
  const fetchCustomerSearch = async (searchTerm) => {
    try {
      const result = await customerService.getCustomers({ search: searchTerm, limit: 20 });
      if (result.success) {
        return result.customers || [];
      }
      return [];
    } catch (error) {
      return [];
    }
  };

  // Fetch CRM team users (same as lead assignment dropdown)
  // Helper to get user display name
  const getUserDisplayName = (person) => {
    if (!person) return 'Unknown User';
    if (typeof person === 'string') return person;
    if (person.name) return person.name;
    if (person.firstName && person.lastName) return `${person.firstName} ${person.lastName}`;
    if (person.firstName) return person.firstName;
    if (person.lastName) return person.lastName;
    if (person.email) return person.email;
    return 'Unknown User';
  };

  // Normalize assignee to a standard shape
  const normalizeAssignee = (person) => ({
    id: person?.user || person?._id || person?.id || '',
    name: getUserDisplayName(person),
    email: person?.email || '',
    role: person?.role || '',
    designation: person?.designation || person?.position || ''
  });

  // Load modal assignees with role-based scoping (same logic as LogCustomerActivity)
  const loadModalAssignees = useCallback(async (search = '') => {
    if (isSalesExecutive) {
      const selfAssignee = normalizeAssignee({
        id: currentUserId,
        name: getUserDisplayName(user),
        email: user?.email || '',
        role: user?.role || (user?.role === USER_ROLES.EXPORT_EXECUTIVE ? USER_ROLES.EXPORT_EXECUTIVE : USER_ROLES.SALES_EXECUTIVE),
        designation: user?.designation || (user?.role === USER_ROLES.EXPORT_EXECUTIVE ? 'Export Executive' : 'Sales Executive')
      });

      setModalAssigneeOptions([selfAssignee]);
      setFilteredModalAssigneeOptions([selfAssignee]);
      setSelectedAssignee(selfAssignee);
      setNewTicket(prev => ({ ...prev, assignee: selfAssignee.id }));
      return [selfAssignee];
    }

    if (!user) return [];

    setLoadingModalAssignees(true);
    try {
      const trimmedSearch = (search || '').trim();
      const params = new URLSearchParams();
      if (trimmedSearch) {
        params.append('search', trimmedSearch);
      }
      params.append('limit', '50');
      params.append('roleFilter', 'sales');

      if (isManager) {
        params.append('managerId', user._id || user.id || currentUserId);
      }

      const url = `${API_ENDPOINTS.CRM.TEAM_MANAGEMENT.USERS}${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await authFetch(url, {
        credentials: 'include',
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();

      if (result.responseCode === 200) {
        const crmUsers = result.data?.users || [];
        const transformedUsers = crmUsers
          .filter((crmUser) => ['sales_manager', 'sales_executive', 'export_manager', 'export_executive', 'business_relationship_manager', 'business_development_executive'].includes((crmUser.role || '').toLowerCase()))
          .map((crmUser) => normalizeAssignee(crmUser));

        // If the logged-in user is a manager, include their name in the options list
        if (isManager) {
          const selfAssignee = normalizeAssignee({
            id: currentUserId,
            name: getUserDisplayName(user),
            email: user?.email || '',
            role: user?.role || '',
            designation: user?.designation || ''
          });

          const exists = transformedUsers.some(u => String(u.id) === String(selfAssignee.id));
          if (!exists) {
            transformedUsers.push(selfAssignee);
          }
        }

        transformedUsers.sort((a, b) => a.name.localeCompare(b.name));

        setModalAssigneeOptions(transformedUsers);
        setFilteredModalAssigneeOptions(transformedUsers);
        return transformedUsers;
      }

      setModalAssigneeOptions([]);
      setFilteredModalAssigneeOptions([]);
      return [];
    } catch (error) {
      setModalAssigneeOptions([]);
      setFilteredModalAssigneeOptions([]);
      return [];
    } finally {
      setLoadingModalAssignees(false);
    }
  }, [currentUserId, isSalesExecutive, isManager, user]);

  // Load modal assignees on mount
  useEffect(() => {
    loadModalAssignees('');
  }, [loadModalAssignees]);

  // Debounced search for modal assignee dropdown
  useEffect(() => {
    if (!openModalAssigneeDropdown && !openEditAssigneeDropdown) return;

    const handler = setTimeout(() => {
      loadModalAssignees(modalAssigneeSearchTerm);
    }, 300);

    return () => clearTimeout(handler);
  }, [modalAssigneeSearchTerm, openModalAssigneeDropdown, openEditAssigneeDropdown, loadModalAssignees]);

  // Calculate modal assignee dropdown position (create modal)
  useEffect(() => {
    if (openModalAssigneeDropdown && modalAssigneeDropdownRef.current) {
      const rect = modalAssigneeDropdownRef.current.getBoundingClientRect();
      setModalAssigneeDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width
      });
    }
  }, [openModalAssigneeDropdown]);

  // Calculate edit assignee dropdown position (edit modal)
  useEffect(() => {
    if (openEditAssigneeDropdown && editAssigneeDropdownRef.current) {
      const rect = editAssigneeDropdownRef.current.getBoundingClientRect();
      setEditAssigneeDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width
      });
    }
  }, [openEditAssigneeDropdown]);

  // Close modal assignee dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        modalAssigneeDropdownRef.current &&
        !modalAssigneeDropdownRef.current.contains(event.target) &&
        !event.target.closest('[data-dropdown-modal-assignee]')
      ) {
        setOpenModalAssigneeDropdown(false);
      }
      if (
        editAssigneeDropdownRef.current &&
        !editAssigneeDropdownRef.current.contains(event.target) &&
        !event.target.closest('[data-dropdown-edit-assignee]')
      ) {
        setOpenEditAssigneeDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Render customer item
  const renderCustomerItem = (customer, isSelected = false) => {
    if (isSelected) {
      return customer.companyName || customer.contactPerson || customer.contactPersonName || '';
    }
    return (
      <div>
        <div className="font-medium text-gray-900">{customer.companyName || customer.name || 'Unnamed'}</div>
        <div className="text-xs text-gray-500">{customer.contactPerson || customer.contactPersonName || customer.email || ''}</div>
      </div>
    );
  };

  // Handle customer selection
  const handleCustomerSelect = (customer) => {
    if (!customer) {
      setSelectedCustomer(null);

      const resetData = {
        customer: '',
        business: '',
        linkedOrder: ''
      };

      if (editingTicket) {
        setEditingTicket(prev => ({ ...prev, ...resetData }));
      } else {
        setNewTicket(prev => ({ ...prev, ...resetData }));
      }
      return;
    }

    setSelectedCustomer(customer);
    const customerName = customer.contactPerson || customer.contactPersonName || customer.companyName;

    const updateData = {
      customer: customerName,
      business: customer.companyName || '',
      linkedOrder: '' // Reset linked invoice when customer changes
    };

    if (editingTicket) {
      setEditingTicket(prev => ({ ...prev, ...updateData }));
    } else {
      setNewTicket(prev => ({ ...prev, ...updateData }));
    }

    if (errors.customer) {
      setErrors(prev => ({ ...prev, customer: '' }));
    }
  };


  // Handle pagination
  const handlePageChange = (page) => {
    setCurrentPage(page);
    setSelectedRows(new Set());
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleItemsPerPageChange = (limit) => {
    setItemsPerPage(limit);
    setCurrentPage(1);
    setSelectedRows(new Set());
  };

  // Handle filter change
  const handleFilterChange = (newFilterStatus) => {
    setFilterStatus(newFilterStatus);
    setCurrentPage(1); // Reset to first page when filter changes
  };

  // Handle priority filter change
  const handlePriorityFilterChange = (newFilterPriority) => {
    setFilterPriority(newFilterPriority);
    setCurrentPage(1); // Reset to first page when filter changes
  };

  // **NEW: Create Ticket Form States**
  const [newTicket, setNewTicket] = useState({
    title: "",
    description: "",
    customer: "",
    business: "",
    priority: "medium",
    category: "general",
    subCategory: "",
    source: "manual",
    assignee: "Support Team",
    linkedOrder: "",
    tags: [],
    attachments: [],
    dueDate: "",
  });

  // Handle column sorting
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Get sorted tickets results
  const getSortedTickets = (data) => {
    if (!sortColumn || !data || data.length === 0) return data;

    return [...data].sort((a, b) => {
      let aValue, bValue;

      if (sortColumn === 'ticketId') {
        // Handle numeric part of ticket ID if possible, otherwise string sort
        aValue = (a.ticketId || a.id || '').toLowerCase();
        bValue = (b.ticketId || b.id || '').toLowerCase();
      } else if (sortColumn === 'title') {
        aValue = (a.title || '').toLowerCase();
        bValue = (b.title || '').toLowerCase();
      } else if (sortColumn === 'customer') {
        aValue = (a.customer || '').toLowerCase();
        bValue = (b.customer || '').toLowerCase();
      } else if (sortColumn === 'priority') {
        // Map priorities to numeric values for correct sorting
        const priorityOrder = { 'high': 3, 'medium': 2, 'low': 1 };
        aValue = priorityOrder[a.priority?.toLowerCase()] || 0;
        bValue = priorityOrder[b.priority?.toLowerCase()] || 0;
      } else if (sortColumn === 'status') {
        aValue = (a.status || '').toLowerCase();
        bValue = (b.status || '').toLowerCase();
      } else if (sortColumn === 'created') {
        aValue = new Date(a.created || 0).getTime();
        bValue = new Date(b.created || 0).getTime();
      } else if (sortColumn === 'assignee') {
        aValue = (a.assignee || '').toLowerCase();
        bValue = (b.assignee || '').toLowerCase();
      } else if (sortColumn === 'dueDate') {
        aValue = new Date(a.dueDate || 0).getTime();
        bValue = new Date(b.dueDate || 0).getTime();
      } else {
        aValue = a[sortColumn] || '';
        bValue = b[sortColumn] || '';
      }

      const order = sortDirection === 'asc' ? 1 : -1;
      if (aValue < bValue) return -1 * order;
      if (aValue > bValue) return 1 * order;
      return 0;
    });
  };

  // Old default tickets removed - now loaded from API
  const [oldTickets] = useState([]);




  // **NEW: Form Options**
  const ticketCategories = [
    { value: "general", label: "General Inquiry" },
    { value: "delivery", label: "Delivery Issue" },
    { value: "quality", label: "Product Quality" },
    { value: "billing", label: "Billing & Payment" },
    { value: "technical", label: "Technical Support" },
    { value: "complaint", label: "Complaint" },
  ];

  const ticketSources = [
    { value: "manual", label: "Manual Entry" },
    { value: "whatsapp", label: "WhatsApp" },
    { value: "email", label: "Email" },
    { value: "call", label: "Phone Call" },
    { value: "website", label: "Website" },
    { value: "mobile_app", label: "Mobile App" },
  ];


  // React Query: Create ticket mutation
  const createTicketMutation = useMutation({
    mutationFn: async (ticketData) => {
      const result = await ticketService.createTicket(ticketData);
      if (result.success) {
        return result;
      }
      throw new Error(result.error || 'Failed to create ticket');
    },
    onSuccess: (result) => {
      // Invalidate queries to refetch data
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
      queryClient.invalidateQueries({ queryKey: ['ticketKPIs'] });

      // Reset form
      setNewTicket({
        title: "",
        description: "",
        customer: "",
        business: "",
        priority: "medium",
        category: "general",
        subCategory: "",
        source: "manual",
        assignee: "",
        linkedOrder: "",
        tags: [],
        attachments: [],
        dueDate: "",
      });
      setSelectedCustomer(null);
      setSelectedAssignee(null);
      setErrors({});
      setShowCreateModal(false);

      if (window._customAlert && window._customAlert.showSuccess) {
        window._customAlert.showSuccess(`Ticket ${result.ticket.ticketId} created successfully!`);
      } else {
        alert(`Ticket ${result.ticket.ticketId} created successfully!`);
      }
    },
    onError: (error) => {
      if (window._customAlert && window._customAlert.showError) {
        window._customAlert.showError(error.message || 'Failed to create ticket');
      } else {
        alert(error.message || 'Failed to create ticket');
      }
    }
  });

  // Handle blur for Create Ticket
  const handleCreateTicketBlur = (field) => {
    const newErrors = { ...errors };
    if (field === 'title') {
      if (!newTicket.title || !newTicket.title.trim()) {
        newErrors.title = 'Title is required';
      }
    } else if (field === 'description') {
      if (!newTicket.description || !newTicket.description.trim()) {
        newErrors.description = 'Description is required';
      }
    } else if (field === 'customer') {
      if (!selectedCustomer) {
        newErrors.customer = 'Customer is required';
      }
    }
    setErrors(newErrors);
  };

  // Handle blur for Update Ticket
  const handleUpdateTicketBlur = (field) => {
    const newErrors = { ...errors };
    if (field === 'title') {
      if (!editingTicket.title || !editingTicket.title.trim()) {
        newErrors.title = 'Title is required';
      }
    } else if (field === 'description') {
      if (!editingTicket.description || !editingTicket.description.trim()) {
        newErrors.description = 'Description is required';
      }
    }
    setErrors(newErrors);
  };

  // **NEW: Create Ticket Handler**
  const handleCreateTicket = async () => {
    // Reset errors
    setErrors({});

    // Validate required fields
    const newErrors = {};

    if (!newTicket.title || !newTicket.title.trim()) {
      newErrors.title = 'Title is required';
    }

    if (!newTicket.description || !newTicket.description.trim()) {
      newErrors.description = 'Description is required';
    }

    if (!selectedCustomer) {
      newErrors.customer = 'Customer is required';
    }

    // If there are errors, set them and return
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      // Scroll to first error field
      setTimeout(() => {
        const firstErrorField = Object.keys(newErrors)[0];
        let errorElement = null;

        if (firstErrorField === 'customer') {
          // For customer dropdown, find the input inside SearchableDropdown
          errorElement = document.querySelector('label:has-text("Customer")')?.nextElementSibling?.querySelector('input') ||
            document.querySelector('[placeholder*="customer" i]');
        } else {
          errorElement = document.querySelector(`[name="${firstErrorField}"]`);
        }

        if (errorElement) {
          errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          errorElement.focus();
        }
      }, 100);
      return;
    }

    const customerId = selectedCustomer._id || selectedCustomer.id;
    // Safely derive orderId - treat "None" / "No orders" as no selection
    const linkedOrderValue = (newTicket.linkedOrder || '').toString().trim();
    const invoiceId =
      linkedOrderValue &&
        linkedOrderValue !== 'none' &&
        linkedOrderValue !== 'no_orders'
        ? linkedOrderValue
        : null;

    const ticketData = {
      title: newTicket.title.trim(),
      description: newTicket.description.trim(),
      customerId: customerId,
      customerName: newTicket.customer.trim(),
      businessName: newTicket.business.trim() || '',
      priority: newTicket.priority,
      category: newTicket.category,
      subCategory: newTicket.subCategory || '',
      source: newTicket.source,
      assignee: newTicket.assignee && newTicket.assignee !== 'none' ? newTicket.assignee : null,
      invoiceId: invoiceId,
      tags: newTicket.tags || [],
      attachments: newTicket.attachments || [],
      dueDate: newTicket.dueDate || null
    };

    createTicketMutation.mutate(ticketData);
  };

  // **NEW: Selection and Export Handlers**

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allIds = tickets.map(t => t.id || t._id || t.ticketId);
      setSelectedRows(new Set(allIds));
    } else {
      setSelectedRows(new Set());
    }
  };

  const handleSelectRow = (id) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedRows(newSelected);
  };

  const requestBackendExport = useCallback(
    async ({
      scope,
      format,
      selectedIds = [],
      overrideFilters = null,
      overridePage = null,
      overrideLimit = null,
    }) => {
      const response = await authFetch(
        `${process.env.REACT_APP_API_BASE_URL}/exports/generate`, {
        credentials: 'include',
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
                      },
          body: JSON.stringify({
            module: 'tickets-feedback',
            format,
            scope,
            filters: overrideFilters ?? {
              search: debouncedSearchTerm.trim() || undefined,
              status: filterStatus !== 'all' ? filterStatus : undefined,
              priority: filterPriority !== 'all' ? filterPriority : undefined,
              startDate: dateRange.startDate || undefined,
              endDate: dateRange.endDate || undefined,
              assignee: filterAssignee !== 'all' ? filterAssignee : undefined,
            },
            page: overridePage ?? currentPage,
            limit: overrideLimit ?? itemsPerPage,
            sortBy: sortColumn,
            sortOrder: sortDirection,
            selectedIds,
          }),
        }
      );

      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || 'Export request failed');
      }

      const blob = await response.blob();
      const disposition = response.headers.get('content-disposition') || '';
      const filenameMatch =
        disposition.match(/filename\*=UTF-8''([^;]+)/i) ||
        disposition.match(/filename="?([^"]+)"?/i);
      const fallbackName = `tickets-export.${format === 'xlsx' ? 'xlsx' : format}`;
      const filename = filenameMatch?.[1]
        ? decodeURIComponent(filenameMatch[1]).replace(/(^"|"$)/g, '')
        : fallbackName;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    [debouncedSearchTerm, filterStatus, filterPriority, dateRange, currentPage, itemsPerPage, sortColumn, sortDirection, filterAssignee]
  );

  const handleExportClick = () => {
    if (selectedRows.size > 0) {
      setShowInlineSelectedFormats((prev) => !prev);
      return;
    }
    setShowInlineSelectedFormats(false);
    setShowExportModal(true);
  };

  const handleTicketsSelectedFormatExport = useCallback(
    async (format) => {
      if (!['csv', 'xlsx', 'pdf'].includes(format)) return;
      if (!selectedRows.size) {
        alert('No selected tickets found to export.');
        return;
      }
      setExportLoading(true);
      try {
        await requestBackendExport({
          scope: 'selected',
          format,
          selectedIds: Array.from(selectedRows),
        });
        setSelectedRows(new Set());
        setShowInlineSelectedFormats(false);
      } catch (error) {
        alert(error?.message || 'Export failed.');
      } finally {
        setExportLoading(false);
      }
    },
    [selectedRows, requestBackendExport]
  );

  const handleTicketsScopedExport = useCallback(
    async (scope, format, meta = {}) => {
      if (!['csv', 'xlsx', 'pdf'].includes(format)) {
        return;
      }

      setExportLoading(true);
      try {
        const params = { scope, format };

        if (scope === 'dateRange' && meta.startDate && meta.endDate) {
          params.overrideFilters = {
            range: 'custom',
            startDate: meta.startDate,
            endDate: meta.endDate,
          };
        } else if (scope === 'all') {
          // "Export All Data" must ignore the currently applied UI filters
          params.overrideFilters = {};
          params.overridePage = 1;
          params.overrideLimit = 100000;
        }

        await requestBackendExport(params);
        setShowExportModal(false);
      } catch (error) {
        alert(error?.message || 'Export failed.');
      } finally {
        setExportLoading(false);
      }
    },
    [requestBackendExport]
  );


  const getPriorityColor = (priority) => {
    switch (priority) {
      case "high":
        return "bg-red-100 text-red-800";
      case "medium":
        return "bg-yellow-100 text-yellow-800";
      case "low":
        return "bg-green-100 text-green-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const formatPriority = (priority) => {
    if (!priority) return "Medium";
    const normalized = priority.toString().toLowerCase();
    return normalized.charAt(0).toUpperCase() + normalized.slice(1);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "open":
        return "bg-blue-100 text-blue-800";
      case "in-progress":
        return "bg-yellow-100 text-yellow-800";
      case "resolved":
        return "bg-green-100 text-green-800";
      case "closed":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  // Capitalize first letter of status for consistent display
  const formatStatus = (status) => {
    if (!status) return "Open";
    const normalized = status.toString().toLowerCase();
    return normalized.charAt(0).toUpperCase() + normalized.slice(1);
  };

  const getSlaStatusColor = (slaStatus) => {
    switch (slaStatus) {
      case "on-track":
        return "bg-green-100 text-green-800";
      case "at-risk":
        return "bg-yellow-100 text-yellow-800";
      case "breached":
        return "bg-red-100 text-red-800";
      case "completed":
        return "bg-blue-100 text-blue-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getSlaStatusIcon = (slaStatus) => {
    switch (slaStatus) {
      case "on-track":
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case "at-risk":
        return <Clock className="h-4 w-4 text-yellow-600" />;
      case "breached":
        return <AlertTriangle className="h-4 w-4 text-red-600" />;
      case "completed":
        return <CheckCircle className="h-4 w-4 text-blue-600" />;
      default:
        return <Clock className="h-4 w-4 text-gray-600" />;
    }
  };

  // Tickets are already filtered and paginated by backend

  const getTimeRemaining = (sla) => {
    const now = new Date();
    const slaDate = new Date(sla);
    const diff = slaDate - now;

    if (diff < 0) return "Overdue";

    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    if (hours > 24) {
      const days = Math.floor(hours / 24);
      return `${days}d ${hours % 24}h remaining`;
    }

    return `${hours}h ${minutes}m remaining`;
  };

  // React Query: Fetch ticket by ID
  const {
    data: ticketDetailData,
    isLoading: loadingTicketDetail
  } = useQuery({
    queryKey: ['ticketDetail', ticketIdForDetail],
    queryFn: async () => {
      if (!ticketIdForDetail) return null;
      const result = await ticketService.getTicketById(ticketIdForDetail);
      if (result.success) {
        return result.ticket;
      }
      throw new Error(result.error || 'Failed to load ticket details');
    },
    enabled: !!ticketIdForDetail
  });

  // Update editingTicket when ticketDetailData changes and edit modal is open
  useEffect(() => {
    if (ticketDetailData && showEditModal) {
      const ticketData = ticketDetailData;

      // Set Customer object for dropdown
      if (ticketData.customerId) {
        const cust = ticketData.customerId;
        setSelectedCustomer({
          companyName: cust.companyName || ticketData.businessName || '',
          contactPerson: cust.contactPerson || cust.name || ticketData.customerName || '',
          name: cust.name || ticketData.customerName || '',
          _id: cust._id || cust.id || ticketData.customerId
        });
      }

      setEditingTicket({
        _id: ticketData._id,
        id: ticketData.ticketId,
        ticketId: ticketData.ticketId,
        title: ticketData.title,
        description: ticketData.description,
        priority: ticketData.priority,
        status: ticketData.status,
        category: ticketData.category || 'general',
        subCategory: ticketData.subCategory || '',
        source: ticketData.source || 'manual',
        resolution: ticketData.resolution || '',
        linkedOrder: ticketData.orderId?._id || ticketData.orderId?.id || ticketData.orderId || '',
        assignee: ticketData.assignee?._id || ticketData.assignee || '',
        assigneeId: ticketData.assignee?._id || ticketData.assignee || '',
        business: ticketData.businessName || '',
        dueDate: ticketData.dueDate ? new Date(ticketData.dueDate).toISOString().split('T')[0] : ''
      });

      // Set Assignee object
      if (ticketData.assignee && typeof ticketData.assignee === 'object') {
        setEditingAssignee(ticketData.assignee);
      }
    }
  }, [ticketDetailData, showEditModal]);

  // React Query: Fetch support users for assignee
  const { data: supportUsersData } = useQuery({
    queryKey: ['supportUsers'],
    queryFn: async () => {
      const result = await userService.getSupportUsers('');
      if (result.success) {
        return result.users || [];
      }
      return [];
    }
  });



  // **NEW: Edit Ticket Handler**
  const handleEditTicket = async (ticket) => {
    const id = ticket._id || ticket.id;
    setTicketIdForDetail(id);

    // Set immediate partial state from the list item so the modal doesn't look empty while loading details
    setEditingTicket({
      _id: ticket._id || ticket.id,
      id: ticket.ticketId || ticket.id,
      title: ticket.title || '',
      description: ticket.description || '',
      priority: ticket.priority || 'medium',
      status: ticket.status || 'open',
      category: ticket.category || 'general',
      subCategory: ticket.subCategory || '',
      source: ticket.source || 'manual',
      resolution: ticket.resolution || '',
      linkedOrder: ticket.linkedOrderId || '',
      assignee: ticket.assigneeId || (ticket.assignee && ticket.assignee._id) || '',
      assigneeId: ticket.assigneeId || (ticket.assignee && ticket.assignee._id) || '',
      customer: ticket.customer || '',
      business: ticket.business || '',
      dueDate: ticket.dueDate ? new Date(ticket.dueDate).toISOString().split('T')[0] : ''
    });

    if (ticket.customer) {
      setSelectedCustomer({
        companyName: ticket.business || '',
        contactPerson: ticket.customer || '',
        name: ticket.customer || '',
        id: ticket.customerId || null,
        _id: ticket.customerId || null
      });
    }

    setShowEditModal(true);
  };

  // Handle view ticket (detail page)
  const handleViewTicket = (ticket) => {
    const id = ticket._id || ticket.id;
    navigate(`/crm/tickets/${id}`);
  };



  // React Query: Delete ticket mutation
  const deleteTicketMutation = useMutation({
    mutationFn: async (ticketId) => {
      const result = await ticketService.deleteTicket(ticketId);
      if (result.success) {
        return result;
      }
      throw new Error(result.error || 'Failed to delete ticket');
    },
    onSuccess: () => {
      // Invalidate queries to refetch data
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
      queryClient.invalidateQueries({ queryKey: ['ticketKPIs'] });

      if (window._customAlert && window._customAlert.showSuccess) {
        window._customAlert.showSuccess('Ticket deleted successfully!');
      } else {
        alert('Ticket deleted successfully!');
      }
    },
    onError: (error) => {
      if (window._customAlert && window._customAlert.showError) {
        window._customAlert.showError(error.message || 'Failed to delete ticket. Please try again.');
      } else {
        alert(error.message || 'Failed to delete ticket. Please try again.');
      }
    }
  });

  // React Query: Bulk delete tickets mutation
  const bulkDeleteTicketsMutation = useMutation({
    mutationFn: async (ticketIds) => {
      const result = await ticketService.bulkDeleteTickets(ticketIds);
      if (result.success) {
        return result;
      }
      throw new Error(result.error || 'Failed to delete tickets');
    },
    onSuccess: () => {
      // Invalidate queries to refetch data
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
      queryClient.invalidateQueries({ queryKey: ['ticketKPIs'] });
      setSelectedRows(new Set());

      if (window._customAlert && window._customAlert.showSuccess) {
        window._customAlert.showSuccess('Tickets deleted successfully!');
      } else {
        alert('Tickets deleted successfully!');
      }
    },
    onError: (error) => {
      if (window._customAlert && window._customAlert.showError) {
        window._customAlert.showError(error.message || 'Failed to delete tickets. Please try again.');
      } else {
        alert(error.message || 'Failed to delete tickets. Please try again.');
      }
    }
  });

  // **NEW: Bulk Delete Handler**
  const handleBulkDelete = async () => {
    if (selectedRows.size === 0) return;
    if (isDeleting || bulkDeleteTicketsMutation.isPending) return;
    setShowBulkDeleteConfirm(true);
  };

  const confirmBulkDelete = () => {
    setIsDeleting(true);
    bulkDeleteTicketsMutation.mutate(Array.from(selectedRows), {
      onSettled: () => {
        setIsDeleting(false);
        setShowBulkDeleteConfirm(false);
      }
    });
  };

  // **NEW: Delete Ticket Handler**
  const handleDeleteTicket = async (id, e, ticketId) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (isDeleting || deleteTicketMutation.isPending) return;

    try {
      const confirmMessage = `Are you sure you want to delete ticket ${ticketId || id}? This action cannot be undone.`;
      const confirmResult = window.confirm(confirmMessage);

      let confirmed = false;
      if (confirmResult instanceof Promise) {
        confirmed = await confirmResult;
      } else {
        confirmed = confirmResult === true;
      }

      if (confirmed === true) {
        setIsDeleting(true);
        deleteTicketMutation.mutate(id);
      }
    } catch (error) {
    } finally {
      setIsDeleting(false);
    }
  };

  // React Query: Update ticket mutation
  const updateTicketMutation = useMutation({
    mutationFn: async ({ ticketId, updateData }) => {
      const result = await ticketService.updateTicket(ticketId, updateData);
      if (result.success) {
        return { result, ticketId };
      }
      throw new Error(result.error || 'Failed to update ticket');
    },
    onSuccess: ({ ticketId }) => {
      // Invalidate queries to refetch data
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
      queryClient.invalidateQueries({ queryKey: ['ticketDetail', ticketId] });

      setShowEditModal(false);
      setEditingTicket(null);
      setEditingAssignee(null);
      setTicketIdForDetail(null);



      alert('Ticket updated successfully!');
    },
    onError: (error) => {
      alert(error.message || 'Failed to update ticket');
    }
  });

  // **NEW: Update Ticket Handler**
  const handleUpdateTicket = async (updatedTicket) => {
    const requiredFields = ['title', 'description'];
    const validation = validateRequiredFields(updatedTicket, requiredFields);

    if (!validation.isValid) {
      setErrors(validation.errors);
      return;
    }

    setErrors({});

    // Use database ID (_id) for the API call if available
    const ticketId = updatedTicket._id || updatedTicket.id || updatedTicket.ticketId;

    // Safely derive invoiceId - treat "None" / "No orders" as no selection
    const linkedInvoiceValue = (updatedTicket.linkedOrder || '').toString().trim();
    const invoiceId =
      linkedInvoiceValue &&
        linkedInvoiceValue !== 'none' &&
        linkedInvoiceValue !== 'no_orders'
        ? linkedInvoiceValue
        : null;

    const updateData = {
      title: updatedTicket.title.trim(),
      description: updatedTicket.description.trim(),
      priority: updatedTicket.priority,
      status: updatedTicket.status,
      // If customer was changed via SearchableDropdown, selectedCustomer will have new ID
      customerId: selectedCustomer?._id || selectedCustomer?.id || updatedTicket.customerId || null,
      customerName: updatedTicket.customer || (selectedCustomer?.name || ''),
      businessName: updatedTicket.business || (selectedCustomer?.companyName || ''),
      assignee: updatedTicket.assigneeId || updatedTicket.assignee || null,
      category: updatedTicket.category || 'general',
      subCategory: updatedTicket.subCategory || '',
      source: updatedTicket.source || 'manual',
      resolution: updatedTicket.resolution || '',
      invoiceId: invoiceId,
      dueDate: updatedTicket.dueDate || null
    };

    updateTicketMutation.mutate({
      ticketId,
      updateData
    });
  };

  return (
    <div className="mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Tickets
              </h1>
              <p className="text-gray-600 mt-2">
                Manage customer support tickets
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            {selectedRows.size > 0 && (
              <PermissionGate permission="customer_management" action="delete">
                <button
                  onClick={handleBulkDelete}
                  disabled={isDeleting || bulkDeleteTicketsMutation.isPending}
                  className="flex items-center px-4 py-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors disabled:opacity-50"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete ({selectedRows.size})
                </button>
              </PermissionGate>
            )}
            <PermissionGate permission="customer_management" action="read">
              <div className="relative" ref={selectedExportWrapRef}>
                <button
                  onClick={handleExportClick}
                  className={`flex items-center px-4 py-2 rounded-lg transition-colors whitespace-nowrap ${selectedRows.size > 0
                      ? 'bg-blue-50 border-2 border-blue-200 text-blue-600'
                      : 'text-gray-700 bg-gray-100 hover:bg-gray-200 border-2 border-transparent'
                    }`}
                  disabled={exportLoading || (!tickets?.length && selectedRows.size === 0)}
                >
                  <Download className="h-4 w-4 mr-2" />
                  {selectedRows.size > 0 ? `Export (${selectedRows.size})` : 'Export'}
                </button>
                <SelectedExportFormatBar
                  isOpen={showInlineSelectedFormats}
                  panelEntered={inlineFormatPanelEntered}
                  onExport={handleTicketsSelectedFormatExport}
                  loading={exportLoading}
                />
              </div>
            </PermissionGate>
            <PermissionGate permission="customer_management" action="create">
              <button
                onClick={() => setShowBulkUpload(true)}
                className="flex items-center px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
              >
                <Upload className="h-4 w-4 mr-2" />
                Bulk Upload
              </button>
            </PermissionGate>
            <PermissionGate permission="customer_management" action="create">
              <button
                onClick={() => setShowCreateModal(true)}
                className="flex items-center px-4 py-2 bg-agrosaf-green text-white rounded-lg hover:bg-agrosaf-darkGreen transition-colors"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create Ticket
              </button>
            </PermissionGate>
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
        <div className="bg-white rounded-lg shadow-sm border p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Total Tickets</p>
              {loadingKPIs ? (
                <div className="h-8 w-16 bg-gray-200 animate-pulse rounded mt-1"></div>
              ) : (
                <p className="text-2xl font-bold text-gray-900">
                  {kpis.totalTickets}
                </p>
              )}
            </div>
            <FileText className="h-8 w-8 text-blue-600" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Open</p>
              {loadingKPIs ? (
                <div className="h-8 w-16 bg-gray-200 animate-pulse rounded mt-1"></div>
              ) : (
                <p className="text-2xl font-bold text-blue-600">
                  {kpis.openTickets}
                </p>
              )}
            </div>
            <Clock className="h-8 w-8 text-blue-600" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">In Progress</p>
              {loadingKPIs ? (
                <div className="h-8 w-16 bg-gray-200 animate-pulse rounded mt-1"></div>
              ) : (
                <p className="text-2xl font-bold text-yellow-600">
                  {kpis.inProgressTickets}
                </p>
              )}
            </div>
            <Clock className="h-8 w-8 text-yellow-600" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Pending</p>
              {loadingKPIs ? (
                <div className="h-8 w-16 bg-gray-200 animate-pulse rounded mt-1"></div>
              ) : (
                <p className="text-2xl font-bold text-orange-600">
                  {kpis.pendingTickets}
                </p>
              )}
            </div>
            <AlertTriangle className="h-8 w-8 text-orange-600" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Resolved</p>
              {loadingKPIs ? (
                <div className="h-8 w-16 bg-gray-200 animate-pulse rounded mt-1"></div>
              ) : (
                <p className="text-2xl font-bold text-green-600">
                  {kpis.resolvedTickets}
                </p>
              )}
            </div>
            <CheckCircle className="h-8 w-8 text-green-600" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Closed</p>
              {loadingKPIs ? (
                <div className="h-8 w-16 bg-gray-200 animate-pulse rounded mt-1"></div>
              ) : (
                <p className="text-2xl font-bold text-gray-600">
                  {kpis.closedTickets}
                </p>
              )}
            </div>
            <XCircle className="h-8 w-8 text-gray-600" />
          </div>
        </div>
      </div>


      {/* Tickets List */}
      <div className="space-y-6">
        {/* Search and Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
          <div className="flex flex-row flex-wrap gap-3 items-center">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search tickets by title, ID, customer, business, or description..."
                value={searchTerm}
                onChange={(e) => handleSearchInputChange(e, setSearchTerm, FIELD_LIMITS.search)}
                maxLength={FIELD_LIMITS.search}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent w-full"
                autoComplete="off"
                autoFocus
              />
            </div>
            {/* Assignee Filter Dropdown */}
            {!isSalesExecutive && (
              <div className="relative">
                <button
                  ref={assigneeDropdownRef}
                  onClick={() => {
                    const isOpening = !openAssigneeDropdown;
                    setOpenAssigneeDropdown(isOpening);
                    if (isOpening) {
                      setAssigneeFilterSearchTerm('');
                    }
                  }}
                  disabled={loadingAssigneeFilter}
                  className="px-3 py-2.5 border border-gray-300 rounded-lg flex items-center justify-between text-left bg-white focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed text-sm min-w-[160px]"
                >
                  <span className="truncate whitespace-nowrap mr-2">
                    {filterAssignee === 'all'
                      ? 'All Assignees'
                      : filterAssignee === 'my_team'
                        ? 'My Team'
                        : assigneeOptions.find(opt => opt.id === filterAssignee)?.name || 'All Assignees'}
                  </span>
                  <ChevronDown className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform ${openAssigneeDropdown ? 'rotate-180' : ''}`} />
                </button>

                {openAssigneeDropdown && (
                  <div
                    data-dropdown-assignee-filter
                    className="fixed z-[9999] bg-white border border-gray-200 rounded-lg shadow-xl"
                    style={{
                      top: `${assigneeDropdownPosition.top}px`,
                      left: `${assigneeDropdownPosition.left}px`,
                      minWidth: '240px',
                      maxWidth: '350px',
                      width: `${Math.max(240, assigneeDropdownPosition.width)}px`
                    }}
                  >
                    <div className="p-2 border-b border-gray-100">
                      <div className="relative">
                        <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          type="text"
                          value={assigneeFilterSearchTerm}
                          onChange={(e) => setAssigneeFilterSearchTerm(e.target.value)}
                          placeholder="Search assignees..."
                          className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                          autoFocus
                        />
                      </div>
                    </div>
                    <div className="overflow-y-auto max-h-[240px] overflow-x-hidden">
                      {loadingAssigneeFilter && filteredAssigneeOptions.length === 0 ? (
                        <div className="p-3 text-sm text-gray-500 text-center">Loading...</div>
                      ) : filteredAssigneeOptions.length === 0 ? (
                        <div className="p-3 text-sm text-gray-500 text-center">No assignees found</div>
                      ) : (
                        <div className="flex flex-col">
                          {filteredAssigneeOptions.map(option => (
                            <button
                              key={option.id}
                              type="button"
                              onClick={() => {
                                setAssigneeOptions(prev => prev.some(o => o.id === option.id) ? prev : [...prev, option]);
                                setFilterAssignee(option.id);
                                setOpenAssigneeDropdown(false);
                              }}
                              className={`w-full text-left px-3 py-2 text-sm border-b border-gray-100 last:border-b-0 flex flex-col ${filterAssignee === option.id ? 'bg-agrosaf-green/10 text-agrosaf-green font-medium' : 'hover:bg-gray-50 text-gray-700'}`}
                            >
                              <div className="truncate font-medium">{option.name}</div>
                              {option.email && <div className="text-xs text-gray-500 truncate">{option.email}</div>}
                              {option.role && option.role !== 'system' && <div className="text-xs text-gray-500 truncate capitalize">{option.role.replace(/_/g, ' ')}</div>}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
            <select
              value={filterStatus}
              onChange={(e) => handleFilterChange(e.target.value)}
              className="flex-shrink-0 min-w-[150px] px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
            >
              <option value="all">All Statuses</option>
              <option value="open">Open</option>
              <option value="in-progress">In Progress</option>
              <option value="pending">Pending</option>
              <option value="resolved">Resolved</option>
              <option value="closed">Closed</option>
            </select>
            <select
              value={filterPriority}
              onChange={(e) => handlePriorityFilterChange(e.target.value)}
              className="flex-shrink-0 min-w-[150px] px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
            >
              <option value="all">All Priorities</option>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
            <DateRangeFilter
              startDate={dateRange.startDate}
              endDate={dateRange.endDate}
              onChange={(newRange) => {
                setDateRange(newRange);
                setCurrentPage(1);
              }}
              allowFutureDates={false}
            />

            {(searchTerm || filterStatus !== 'all' || filterPriority !== 'all' || dateRange.startDate || dateRange.endDate || filterAssignee !== defaultAssignee) && (
              <Button
                variant="outline"
                icon={<X className="h-4 w-4" />}
                onClick={() => {
                  setSearchTerm('');
                  setDebouncedSearchTerm('');
                  setFilterStatus('all');
                  setFilterPriority('all');
                  setDateRange({ startDate: '', endDate: '' });
                  setFilterAssignee(defaultAssignee);
                  setAssigneeFilterSearchTerm('');
                  setOpenAssigneeDropdown(false);
                  setCurrentPage(1);
                  if (searchInputRef.current) {
                    searchInputRef.current.focus();
                  }
                }}
                className="flex-shrink-0 whitespace-nowrap"
              >
                Clear
              </Button>
            )}
          </div>
        </div>

        {/* Tickets List */}
        <div className="bg-white rounded-lg shadow-sm border">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-10">
                    <input
                      type="checkbox"
                      className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      checked={tickets.length > 0 && selectedRows.size === tickets.length}
                      onChange={handleSelectAll}
                    />
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('ticketId')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Ticket ID</span>
                      {sortColumn === 'ticketId' ? (
                        sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('title')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Title</span>
                      {sortColumn === 'title' ? (
                        sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('customer')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Customer</span>
                      {sortColumn === 'customer' ? (
                        sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('priority')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Priority</span>
                      {sortColumn === 'priority' ? (
                        sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('status')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Status</span>
                      {sortColumn === 'status' ? (
                        sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('created')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Created Date</span>
                      {sortColumn === 'created' ? (
                        sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('dueDate')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Due Date</span>
                      {sortColumn === 'dueDate' ? (
                        sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('assignee')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Assignee</span>
                      {sortColumn === 'assignee' ? (
                        sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loadingTickets ? (
                  // Loading skeletons
                  Array.from({ length: itemsPerPage }).map((_, idx) => (
                    <tr key={`skeleton-${idx}`} className="animate-pulse">
                      <td className="px-6 py-4"><div className="h-4 w-4 bg-gray-200 rounded"></div></td>
                      <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-24"></div></td>
                      <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-40"></div></td>
                      <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-32"></div></td>
                      <td className="px-6 py-4"><div className="h-6 bg-gray-200 rounded w-16"></div></td>
                      <td className="px-6 py-4"><div className="h-6 bg-gray-200 rounded w-16"></div></td>
                      <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-20"></div></td>
                      <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-24"></div></td>
                      <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-16"></div></td>
                    </tr>
                  ))
                ) : tickets.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="px-6 py-12 text-center text-gray-500">
                      No tickets found matching your criteria.
                    </td>
                  </tr>
                ) : (
                  getSortedTickets(tickets).map((ticket) => {
                    const customerFull = ticket.customer || '';
                    const businessFull = ticket.business || '';
                    const assigneeFull = ticket.assignee || '';
                    const ticketIdFull = ticket.ticketId || '';
                    const titleFull = ticket.title || '';

                    return (
                      <tr key={ticket.id || ticket._id} className="hover:bg-gray-50 border-b border-gray-100 last:border-0 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="checkbox"
                            className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                            checked={selectedRows.has(ticket.id || ticket._id || ticket.ticketId)}
                            onChange={() => handleSelectRow(ticket.id || ticket._id || ticket.ticketId)}
                          />
                        </td>
                        <td className="px-6 py-4">
                          <div
                            className="text-sm font-medium text-gray-900 truncate max-w-[120px]"
                            title={ticketIdFull}
                          >
                            {ticketIdFull}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div
                            className="text-sm font-medium text-gray-900 truncate max-w-[200px] capitalize"
                            title={titleFull}
                          >
                            {truncateText(titleFull, 50)}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="max-w-[200px]">
                            <div
                              className="text-sm font-medium text-gray-900 truncate"
                              title={customerFull}
                            >
                              {customerFull ? customerFull.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ') : ''}
                            </div>
                            <div
                              className="text-sm text-gray-500 truncate"
                              title={businessFull}
                            >
                              {businessFull ? businessFull.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ') : ''}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`px-2 py-1 text-xs rounded-full ${getPriorityColor(ticket.priority)}`}
                            title={ticket.priority ? `${formatPriority(ticket.priority)} Priority` : ''}
                          >
                            {formatPriority(ticket.priority)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`px-2 py-1 text-xs rounded-full ${getStatusColor(ticket.status)}`}
                            title={ticket.status ? `${formatStatus(ticket.status)} Status` : ''}
                          >
                            {formatStatus(ticket.status)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {formatDate(ticket.created, { includeTime: true })}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className={`text-sm flex items-center gap-2 flex-col ${ticket.dueDate && new Date(ticket.dueDate) < new Date() && !['resolved', 'closed'].includes(ticket.status) ? 'text-red-600 font-bold' : 'text-gray-900'
                            }`}>
                            {ticket.dueDate ? formatDate(ticket.dueDate, { includeTime: false }) : ''}
                            {ticket.dueDate && new Date(ticket.dueDate) < new Date() && !['resolved', 'closed'].includes(ticket.status) && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                                Overdue
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="max-w-[200px]">
                            <div
                              className="text-sm font-medium text-gray-900 truncate"
                              title={assigneeFull}
                            >
                              {assigneeFull}
                            </div>
                            {ticket.assigneeEmail && (
                              <div
                                className="text-xs text-gray-500 truncate"
                                title={ticket.assigneeEmail}
                              >
                                {ticket.assigneeEmail}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center space-x-3">
                            <PermissionGate permission="customer_management" action="read">
                              <button
                                onClick={() => handleViewTicket(ticket)}
                                className="text-blue-600 hover:text-blue-900"
                                title="View Ticket"
                              >
                                <Eye className="h-4 w-4" />
                              </button>
                            </PermissionGate>
                            {!['resolved', 'closed'].includes(ticket.status) && (
                              <PermissionGate permission="customer_management" action="update">
                                <button
                                  onClick={() => handleEditTicket(ticket)}
                                  className="text-green-600 hover:text-green-900"
                                  title="Edit Ticket"
                                >
                                  <Edit className="h-4 w-4" />
                                </button>
                              </PermissionGate>
                            )}
                            <PermissionGate permission="customer_management" action="delete">
                              <button
                                onClick={(e) => handleDeleteTicket(ticket._id || ticket.id, e, ticket.ticketId)}
                                disabled={isDeleting || deleteTicketMutation.isPending}
                                className="text-red-600 hover:text-red-900 disabled:opacity-50 disabled:cursor-not-allowed"
                                title="Delete Ticket"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </PermissionGate>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        {
          pagination && pagination.totalPages > 1 && (
            <div className="bg-white rounded-lg border border-gray-200 p-4 mt-6">
              <div className="flex flex-col sm:flex-row items-center justify-between space-y-4 sm:space-y-0">
                {/* Results Count and Limit Selector */}
                <div className="flex items-center space-x-4">
                  <div className="text-sm text-gray-700">
                    Showing {((pagination.currentPage || 1) - 1) * pagination.limit + 1} to {Math.min((pagination.currentPage || 1) * pagination.limit, pagination.totalItems)} of {pagination.totalItems} results
                  </div>
                  <div className="flex items-center space-x-2">
                    <label className="text-sm text-gray-700">Items per page:</label>
                    <select
                      value={itemsPerPage}
                      onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                      className="px-2 py-1 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                    >
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                  </div>
                </div>

                {/* Pagination Controls */}
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handlePageChange((pagination.currentPage || 1) - 1)}
                    disabled={!pagination.hasPrevPage || (pagination.currentPage || 1) <= 1}
                    className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Previous
                  </button>

                  {/* Page Numbers */}
                  <div className="flex items-center space-x-1">
                    {(() => {
                      const totalPages = pagination.totalPages || 1;
                      const currentPageNum = pagination.currentPage || 1;
                      const pages = [];

                      if (totalPages <= 10) {
                        for (let i = 1; i <= totalPages; i++) {
                          pages.push(i);
                        }
                      } else {
                        pages.push(1);

                        if (currentPageNum <= 4) {
                          for (let i = 2; i <= 5; i++) {
                            pages.push(i);
                          }
                          pages.push('ellipsis');
                          pages.push(totalPages);
                        } else if (currentPageNum >= totalPages - 3) {
                          pages.push('ellipsis');
                          for (let i = totalPages - 4; i <= totalPages; i++) {
                            pages.push(i);
                          }
                        } else {
                          pages.push('ellipsis');
                          for (let i = currentPageNum - 1; i <= currentPageNum + 1; i++) {
                            pages.push(i);
                          }
                          pages.push('ellipsis');
                          pages.push(totalPages);
                        }
                      }

                      return pages.map((page, index) => {
                        if (page === 'ellipsis') {
                          return (
                            <span key={`ellipsis-${index}`} className="px-2 text-gray-500">
                              ...
                            </span>
                          );
                        }
                        return (
                          <button
                            key={page}
                            onClick={() => handlePageChange(page)}
                            className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${currentPageNum === page
                              ? 'bg-agrosaf-green text-white'
                              : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                              }`}
                          >
                            {page}
                          </button>
                        );
                      });
                    })()}
                  </div>

                  <button
                    onClick={() => handlePageChange((pagination.currentPage || 1) + 1)}
                    disabled={!pagination.hasNextPage || (pagination.currentPage || 1) >= (pagination.totalPages || 1)}
                    className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )
        }
      </div >

      {/* **NEW: Create Ticket Modal** */}
      {
        showCreateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] !mt-0 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
              <div className="px-6 py-4 border-b border-gray-200 flex-shrink-0">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Create New Ticket
                  </h3>
                  <button
                    onClick={() => {
                      setShowCreateModal(false);
                      setErrors({});
                      setNewTicket({
                        title: "",
                        description: "",
                        customer: "",
                        business: "",
                        priority: "medium",
                        category: "general",
                        subCategory: "",
                        source: "manual",
                        assignee: "",
                        linkedOrder: "",
                        tags: [],
                        attachments: [],
                      });
                      setSelectedCustomer(null);
                      setSelectedAssignee(null);
                      setOpenModalAssigneeDropdown(false);
                      setModalAssigneeSearchTerm('');
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>
              </div>

              <div className="p-6 overflow-y-auto flex-1" style={{ minHeight: 0 }}>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Title <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="title"
                        value={newTicket.title}
                        onChange={(e) => {
                          const sanitized = handleTextInputChange(e, null, 100);
                          setNewTicket({ ...newTicket, title: sanitized });
                          if (errors.title) {
                            setErrors(prev => ({ ...prev, title: '' }));
                          }
                        }}
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${errors.title ? 'border-red-500' : 'border-gray-300'
                          }`}
                        placeholder="Enter ticket title"
                        maxLength={100}
                        required
                        onBlur={() => handleCreateTicketBlur('title')}
                      />
                      <div className="flex items-start justify-between mt-1 gap-2">
                        {errors.title ? (
                          <p className="text-xs text-red-600 flex-1">{errors.title}</p>
                        ) : (
                          <div></div>
                        )}
                        <div className="text-xs text-gray-500 whitespace-nowrap">
                          {getCharacterCount(newTicket.title, 100)}
                        </div>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Priority
                      </label>
                      <select
                        value={newTicket.priority}
                        onChange={(e) =>
                          setNewTicket({ ...newTicket, priority: e.target.value })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                      >
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Description <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      name="description"
                      value={newTicket.description}
                      onChange={(e) => {
                        const sanitized = handleTextInputChange(e, null, FIELD_LIMITS.notes);
                        setNewTicket({
                          ...newTicket,
                          description: sanitized,
                        });
                        if (errors.description) {
                          setErrors(prev => ({ ...prev, description: '' }));
                        }
                      }}
                      rows={3}
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${errors.description ? 'border-red-500' : 'border-gray-300'
                        }`}
                      placeholder="Describe the issue or request in detail..."
                      maxLength={FIELD_LIMITS.notes}
                      required
                      onBlur={() => handleCreateTicketBlur('description')}
                    />
                    <div className="flex items-start justify-between mt-1 gap-2">
                      {errors.description ? (
                        <p className="text-xs text-red-600 flex-1 break-words">{errors.description}</p>
                      ) : (
                        <div></div>
                      )}
                      <div className="text-xs text-gray-500 whitespace-nowrap">
                        {getCharacterCount(newTicket.description, FIELD_LIMITS.notes)}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <SearchableDropdown
                        label="Customer"
                        placeholder="Search customer..."
                        selected={selectedCustomer}
                        onSelect={handleCustomerSelect}
                        fetchDefault={fetchTopCustomers}
                        fetchSearch={fetchCustomerSearch}
                        renderItem={renderCustomerItem}
                        required={true}
                        error={errors.customer}
                        onBlur={() => handleCreateTicketBlur('customer')}
                      />
                      {/* {errors.customer && (
                        <p className="text-xs text-red-600 mt-1">{errors.customer}</p>
                      )} */}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Business Name
                      </label>
                      <input
                        type="text"
                        value={newTicket.business}
                        readOnly
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50"
                        placeholder="Auto-filled from customer"
                      />
                    </div>
                  </div>

                  {/* Linked Order, Category, Source, Assignee - 2 fields per row */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Linked Invoice
                      </label>
                      <select
                        value={newTicket.linkedOrder}
                        onChange={(e) =>
                          setNewTicket({ ...newTicket, linkedOrder: e.target.value })
                        }
                        disabled={!selectedCustomer || loadingInvoices}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                      >
                        <option value="">None</option>
                        {loadingInvoices ? (
                          <option value="" disabled>Loading invoices...</option>
                        ) : customerInvoices.length === 0 ? (
                          <option value="" disabled>No invoices found</option>
                        ) : (
                          customerInvoices.map((inv) => (
                            <option key={inv._id} value={inv._id}>
                              {inv.invoiceNumber} (Amt: {inv.totalAmount?.toLocaleString('en-IN') || 0})
                            </option>
                          ))
                        )}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Category
                      </label>
                      <select
                        value={newTicket.category}
                        onChange={(e) =>
                          setNewTicket({ ...newTicket, category: e.target.value })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                      >
                        {ticketCategories.map((category) => (
                          <option key={category.value} value={category.value}>
                            {category.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Source
                      </label>
                      <select
                        value={newTicket.source}
                        onChange={(e) =>
                          setNewTicket({ ...newTicket, source: e.target.value })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                      >
                        {ticketSources.map((source) => (
                          <option key={source.value} value={source.value}>
                            {source.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Assignee
                      </label>
                      {isSalesExecutive ? (
                        <input
                          type="text"
                          value={selectedAssignee?.name || 'Assigned to you'}
                          readOnly
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm"
                        />
                      ) : (
                        <div className="relative" ref={modalAssigneeDropdownRef}>
                          <button
                            type="button"
                            onClick={() => {
                              const isOpening = !openModalAssigneeDropdown;
                              setOpenModalAssigneeDropdown(isOpening);
                              if (isOpening && modalAssigneeOptions.length === 0) {
                                loadModalAssignees(modalAssigneeSearchTerm);
                              }
                            }}
                            disabled={loadingModalAssignees}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg flex items-center justify-between text-left bg-white focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                          >
                            <span className="truncate text-sm whitespace-nowrap">
                              {selectedAssignee?.name || 'Select Assignee'}
                            </span>
                            <ChevronDown className={`w-4 h-4 text-gray-400 flex-shrink-0 ml-2 transition-transform ${openModalAssigneeDropdown ? 'rotate-180' : ''}`} />
                          </button>

                          {openModalAssigneeDropdown && (
                            <div
                              data-dropdown-modal-assignee
                              className="fixed z-[9999] bg-white border border-gray-200 rounded-lg shadow-xl"
                              style={{
                                top: `${modalAssigneeDropdownPosition.top}px`,
                                left: `${modalAssigneeDropdownPosition.left}px`,
                                minWidth: '240px',
                                maxWidth: '350px',
                                width: `${Math.max(240, modalAssigneeDropdownPosition.width)}px`
                              }}
                            >
                              <div className="p-2 border-b border-gray-100">
                                <div className="relative">
                                  <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                                  <input
                                    type="text"
                                    value={modalAssigneeSearchTerm}
                                    onChange={(e) => {
                                      const value = e.target.value.replace(/[^a-zA-Z0-9\s@._+-]/g, '').slice(0, 100);
                                      setModalAssigneeSearchTerm(value);
                                    }}
                                    placeholder="Search assignees..."
                                    maxLength={100}
                                    className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                                    autoFocus
                                  />
                                </div>
                              </div>

                              <div className="overflow-y-auto max-h-[240px] overflow-x-hidden">
                                {loadingModalAssignees && filteredModalAssigneeOptions.length === 0 ? (
                                  <div className="p-3 text-sm text-gray-500 text-center">
                                    Loading assignees...
                                  </div>
                                ) : filteredModalAssigneeOptions.length === 0 ? (
                                  <div className="p-3 text-sm text-gray-500 text-center">
                                    {modalAssigneeSearchTerm ? 'No assignees found' : 'No assignees available'}
                                  </div>
                                ) : (
                                  <div className="flex flex-col">
                                    {filteredModalAssigneeOptions.map((option) => (
                                      <button
                                        key={option.id}
                                        type="button"
                                        onClick={() => {
                                          setSelectedAssignee(option);
                                          setNewTicket(prev => ({ ...prev, assignee: option.id }));
                                          setOpenModalAssigneeDropdown(false);
                                          setModalAssigneeSearchTerm('');
                                        }}
                                        className={`w-full text-left px-3 py-2 text-sm border-b border-gray-100 last:border-b-0 flex flex-col ${selectedAssignee?.id === option.id
                                          ? 'bg-agrosaf-green/10 text-agrosaf-green font-medium'
                                          : 'hover:bg-gray-50 text-gray-700'
                                          }`}
                                      >
                                        <div className="truncate font-medium break-words">{option.name}</div>
                                        {option.email && (
                                          <div className="text-xs text-gray-500 truncate break-words">{option.email}</div>
                                        )}
                                        {option.role && (
                                          <div className="text-xs text-gray-500 truncate capitalize break-words">{option.role.replace(/_/g, ' ')}</div>
                                        )}
                                      </button>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Due Date
                      </label>
                      <input
                        type="date"
                        value={newTicket.dueDate}
                        min={today}
                        onChange={(e) => setNewTicket({ ...newTicket, dueDate: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="px-6 py-4 border-t border-gray-200 flex-shrink-0">
                <div className="flex items-center justify-end space-x-3">
                  <button
                    onClick={() => {
                      setShowCreateModal(false);
                      setErrors({});
                      setNewTicket({
                        title: "",
                        description: "",
                        customer: "",
                        business: "",
                        priority: "medium",
                        category: "general",
                        subCategory: "",
                        source: "manual",
                        assignee: "",
                        linkedOrder: "",
                        tags: [],
                        attachments: [],
                      });
                      setSelectedCustomer(null);
                      setSelectedAssignee(null);
                      setOpenModalAssigneeDropdown(false);
                      setModalAssigneeSearchTerm('');
                    }}
                    className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <PermissionGate permission="customer_management" action="create">
                    <button
                      onClick={handleCreateTicket}
                      disabled={createTicketMutation.isPending}
                      className="px-4 py-2 bg-agrosaf-green text-white rounded-lg hover:bg-agrosaf-darkGreen disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {createTicketMutation.isPending ? 'Creating...' : 'Create Ticket'}
                    </button>
                  </PermissionGate>
                </div>
              </div>
            </div>
          </div>
        )
      }



      {/* **NEW: Edit Ticket Modal** */}
      {
        showEditModal && editingTicket && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Edit Ticket - {editingTicket.id}
                  </h3>
                  <button
                    onClick={() => {
                      setShowEditModal(false);
                      setEditingTicket(null);
                      setEditingAssignee(null);
                      setSelectedCustomer(null);
                      setTicketIdForDetail(null);
                      setErrors({});
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>
              </div>

              <div className="px-6 py-4">
                <div className="space-y-4">
                  {/* Title */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Title <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        value={editingTicket.title}
                        onChange={(e) => {
                          const sanitized = handleTextInputChange(e, null, FIELD_LIMITS.title);
                          setEditingTicket(prev => ({ ...prev, title: sanitized }));
                          if (errors.title) {
                            setErrors(prev => ({ ...prev, title: '' }));
                          }
                        }}
                        className={`w-full px-3 py-2 pr-16 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${errors.title ? 'border-red-500' : 'border-gray-300'
                          }`}
                        placeholder="Enter ticket title"
                        maxLength={FIELD_LIMITS.title}
                        required
                        onBlur={() => handleUpdateTicketBlur('title')}
                      />
                      <div className="absolute right-2 top-1/2 transform -translate-y-1/2 text-xs text-gray-500">
                        {getCharacterCount(editingTicket.title, FIELD_LIMITS.title)}
                      </div>
                    </div>
                    {errors.title && (
                      <p className="text-xs text-red-600 mt-1">{errors.title}</p>
                    )}
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Description <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <textarea
                        value={editingTicket.description}
                        onChange={(e) => {
                          const sanitized = handleTextInputChange(e, null, FIELD_LIMITS.notes);
                          setEditingTicket(prev => ({ ...prev, description: sanitized }));
                          if (errors.description) {
                            setErrors(prev => ({ ...prev, description: '' }));
                          }
                        }}
                        rows={4}
                        className={`w-full px-3 py-2 pr-16 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${errors.description ? 'border-red-500' : 'border-gray-300'
                          }`}
                        placeholder="Enter ticket description"
                        maxLength={FIELD_LIMITS.notes}
                        required
                        onBlur={() => handleUpdateTicketBlur('description')}
                      />
                      <div className="absolute right-2 bottom-2 text-xs text-gray-500">
                        {getCharacterCount(editingTicket.description, FIELD_LIMITS.notes)}
                      </div>
                    </div>
                    {errors.description && (
                      <p className="text-xs text-red-600 mt-1">{errors.description}</p>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Priority */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Priority
                      </label>
                      <select
                        value={editingTicket.priority}
                        onChange={(e) => setEditingTicket(prev => ({ ...prev, priority: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                      >
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                      </select>
                    </div>

                    {/* Status */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Status
                      </label>
                      <select
                        value={editingTicket.status}
                        onChange={(e) => setEditingTicket(prev => ({ ...prev, status: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                      >
                        <option value="open">Open</option>
                        <option value="in-progress">In Progress</option>
                        <option value="pending">Pending</option>
                        <option value="resolved">Resolved</option>
                        <option value="closed">Closed</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Customer (Read Only in Edit mostly, or Editable? Let's make it match create but maybe read-only if logic requires) */}
                    {/* User requested to use Create form, so we make it editable */}
                    <div>
                      <SearchableDropdown
                        label="Customer"
                        placeholder="Search customer..."
                        selected={selectedCustomer}
                        onSelect={handleCustomerSelect}
                        fetchDefault={fetchTopCustomers}
                        fetchSearch={fetchCustomerSearch}
                        renderItem={renderCustomerItem}
                        required={true}
                        error={errors.customer}
                      // Note: Customer might be locked in edit in some systems, but user asked for Create form consistency
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Business Name
                      </label>
                      <input
                        type="text"
                        value={editingTicket.business || ''}
                        readOnly
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50"
                        placeholder="Auto-filled"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Category */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Category
                      </label>
                      <select
                        value={editingTicket.category}
                        onChange={(e) =>
                          setEditingTicket(prev => ({ ...prev, category: e.target.value }))
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                      >
                        {ticketCategories.map((category) => (
                          <option key={category.value} value={category.value}>
                            {category.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Source */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Source
                      </label>
                      <select
                        value={editingTicket.source}
                        onChange={(e) =>
                          setEditingTicket(prev => ({ ...prev, source: e.target.value }))
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                      >
                        {ticketSources.map((source) => (
                          <option key={source.value} value={source.value}>
                            {source.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Linked Invoice */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Linked Invoice
                      </label>
                      <select
                        value={editingTicket.linkedOrder || ''}
                        onChange={(e) => setEditingTicket(prev => ({ ...prev, linkedOrder: e.target.value }))}
                        disabled={loadingInvoices}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                      >
                        <option value="">None</option>
                        {loadingInvoices ? (
                          <option value="" disabled>Loading invoices...</option>
                        ) : customerInvoices.length === 0 ? (
                          <option value="" disabled>No invoices found</option>
                        ) : (
                          customerInvoices.map((inv) => (
                            <option key={inv._id} value={inv._id}>
                              {inv.invoiceNumber} (Amt: {inv.totalAmount?.toLocaleString('en-IN') || 0})
                            </option>
                          ))
                        )}
                      </select>
                    </div>

                    {/* Assignee */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Assignee
                      </label>
                      {isSalesExecutive ? (
                        <input
                          type="text"
                          value={editingAssignee?.name || 'Assigned to you'}
                          readOnly
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm"
                        />
                      ) : (
                        <div className="relative" ref={editAssigneeDropdownRef}>
                          <button
                            type="button"
                            onClick={() => {
                              const isOpening = !openEditAssigneeDropdown;
                              setOpenEditAssigneeDropdown(isOpening);
                              if (isOpening && modalAssigneeOptions.length === 0) {
                                loadModalAssignees(modalAssigneeSearchTerm);
                              }
                            }}
                            disabled={loadingModalAssignees}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg flex items-center justify-between text-left bg-white focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                          >
                            <span className="truncate text-sm whitespace-nowrap">
                              {editingAssignee?.name || 'Select Assignee'}
                            </span>
                            <ChevronDown className={`w-4 h-4 text-gray-400 flex-shrink-0 ml-2 transition-transform ${openEditAssigneeDropdown ? 'rotate-180' : ''}`} />
                          </button>

                          {openEditAssigneeDropdown && (
                            <div
                              data-dropdown-edit-assignee
                              className="fixed z-[9999] bg-white border border-gray-200 rounded-lg shadow-xl"
                              style={{
                                top: `${editAssigneeDropdownPosition.top}px`,
                                left: `${editAssigneeDropdownPosition.left}px`,
                                minWidth: '240px',
                                maxWidth: '350px',
                                width: `${Math.max(240, editAssigneeDropdownPosition.width)}px`
                              }}
                            >
                              <div className="p-2 border-b border-gray-100">
                                <div className="relative">
                                  <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                                  <input
                                    type="text"
                                    value={modalAssigneeSearchTerm}
                                    onChange={(e) => {
                                      const value = e.target.value.replace(/[^a-zA-Z0-9\s@._+-]/g, '').slice(0, 100);
                                      setModalAssigneeSearchTerm(value);
                                    }}
                                    placeholder="Search assignees..."
                                    maxLength={100}
                                    className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                                    autoFocus
                                  />
                                </div>
                              </div>

                              <div className="overflow-y-auto max-h-[240px] overflow-x-hidden">
                                {loadingModalAssignees && filteredModalAssigneeOptions.length === 0 ? (
                                  <div className="p-3 text-sm text-gray-500 text-center">
                                    Loading assignees...
                                  </div>
                                ) : filteredModalAssigneeOptions.length === 0 ? (
                                  <div className="p-3 text-sm text-gray-500 text-center">
                                    {modalAssigneeSearchTerm ? 'No assignees found' : 'No assignees available'}
                                  </div>
                                ) : (
                                  <div className="flex flex-col">
                                    {filteredModalAssigneeOptions.map((option) => (
                                      <button
                                        key={option.id}
                                        type="button"
                                        onClick={() => {
                                          const normalizedOption = { ...option, _id: option.id };
                                          setEditingAssignee(normalizedOption);
                                          setEditingTicket(prev => ({
                                            ...prev,
                                            assignee: option.id,
                                            assigneeId: option.id
                                          }));
                                          setOpenEditAssigneeDropdown(false);
                                          setModalAssigneeSearchTerm('');
                                        }}
                                        className={`w-full text-left px-3 py-2 text-sm border-b border-gray-100 last:border-b-0 flex flex-col ${editingAssignee?.id === option.id || editingAssignee?._id === option.id
                                          ? 'bg-agrosaf-green/10 text-agrosaf-green font-medium'
                                          : 'hover:bg-gray-50 text-gray-700'
                                          }`}
                                      >
                                        <div className="truncate font-medium break-words">{option.name}</div>
                                        {option.email && (
                                          <div className="text-xs text-gray-500 truncate break-words">{option.email}</div>
                                        )}
                                        {option.role && (
                                          <div className="text-xs text-gray-500 truncate capitalize break-words">{option.role.replace(/_/g, ' ')}</div>
                                        )}
                                      </button>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Due Date
                      </label>
                      <input
                        type="date"
                        value={editingTicket.dueDate || ''}
                        min={today}
                        onChange={(e) => setEditingTicket(prev => ({ ...prev, dueDate: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                      />
                    </div>
                  </div>

                  {/* Resolution (Specific to Edit) */}
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Resolution Note
                    </label>
                    <textarea
                      value={editingTicket.resolution || ''}
                      onChange={(e) => {
                        const sanitized = handleTextInputChange(e, null, FIELD_LIMITS.notes);
                        setEditingTicket(prev => ({ ...prev, resolution: sanitized }));
                      }}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                      placeholder="Enter resolution details if resolving/closing..."
                      maxLength={FIELD_LIMITS.notes}
                    />
                  </div>
                </div>

                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    onClick={() => {
                      setShowEditModal(false);
                      setEditingTicket(null);
                      setEditingAssignee(null);
                      setSelectedCustomer(null);
                      setTicketIdForDetail(null);
                      setErrors({});
                    }}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Cancel
                  </button>
                  <PermissionGate permission="customer_management" action="update">
                    <button
                      onClick={() => handleUpdateTicket(editingTicket)}
                      disabled={updateTicketMutation.isPending}
                      className="px-4 py-2 bg-agrosaf-green text-white rounded-lg hover:bg-agrosaf-darkGreen transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {updateTicketMutation.isPending ? 'Updating...' : 'Update Ticket'}
                    </button>
                  </PermissionGate>
                </div>
              </div>
            </div>
          </div>
        )
      }
      {/* Export Modal */}
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        loading={exportLoading}
        title="Export Tickets"
        showDateRange
        scopedFormatExport={{
          onExport: handleTicketsScopedExport,
          formats: ['csv', 'xlsx', 'pdf'],
          showCurrentPage: true,
          showFiltered: true,
          showDateRange: true,
          showAll: true,
        }}
      />

      <ConfirmationDialog
        isOpen={showBulkDeleteConfirm}
        onClose={() => setShowBulkDeleteConfirm(false)}
        onConfirm={confirmBulkDelete}
        title="Confirm Bulk Delete"
        message={`Are you sure you want to delete ${selectedRows.size} selected ticket(s)? This action cannot be undone.`}
        confirmText="Delete All"
        type="danger"
      />
      {/* Bulk Upload Modal */}
      <BulkTicketUpload
        isOpen={showBulkUpload}
        onCancel={() => setShowBulkUpload(false)}
        onComplete={() => {
          queryClient.invalidateQueries({ queryKey: ['tickets'] });
          queryClient.invalidateQueries({ queryKey: ['ticketKPIs'] });
        }}
      />
    </div>
  );
};

export default TicketsFeedback;
